/**
 * Query-record sections for the Selection Details panel (ADR-038 Phase G).
 *
 * What a Query row expands into: every attribute the record's type
 * declares, plus the shared SOURCE BYTES drawer
 * (:func:`SourceBytesSection`).
 *
 * # Scope, since 2026-10-05
 *
 * This is now the view for query records whose type has **no entity
 * kind** — the status samples, raw blobs and the system-status types. A
 * row of a type that *does* correspond to an entity (detection, alert,
 * registration, track) renders that entity's own sections instead, so the
 * pane is identical whichever surface the operator reached the record
 * from (REQ-UI-183; see ``entity-details/entity-record-mapping.ts``).
 *
 * # Deliberate design points
 *
 * - **The detail renders through the same formatter as the table**
 *   (:func:`renderQueryValue`), so an operator comparing a row to its
 *   detail never sees one field rendered two ways.
 * - **The byte drawer is shared, not reimplemented.** It moved to
 *   ``query/source-bytes-section.tsx`` when four more panes needed it;
 *   the disclosure boundary, fail-safe integrity badge, window paging,
 *   complete text rendering and blocked-download reason all live there.
 *
 * Cross-references:
 *   - REQ-UI-123 (attributes + hex drawer), REQ-UI-183 (one pane per
 *     record, whichever surface reached it), REQ-UI-184 / -185 (the
 *     shared section).
 *   - REQ-QUERY-040..042, 044, 045, 049, 057; feature spec §2.7.
 */
import { SectionShell } from "@/entity-details/section-shell";
import { useProducerNames } from "@/lib/use-producer-names";
import { renderQueryValue } from "@/query/query-columns";
import { attributeMap, type QueryAttribute } from "@/query/query-model";
import { SourceBytesSection } from "@/query/source-bytes-section";
import { useQueryCatalog } from "@/query/use-query-data";
import { useRecordDetail } from "@/query/use-record-detail";

export interface RecordSectionsProps {
  /** Queryable type id from the catalog. */
  readonly typeId: string;
  /** The record's natural id within that type. */
  readonly recordId: string;
}

/** Render the sections for one selected query record. */
export function RecordSections({ typeId, recordId }: RecordSectionsProps): React.ReactElement {
  const catalog = useQueryCatalog();
  const detail = useRecordDetail(typeId, recordId);
  const producerNames = useProducerNames();

  const type = catalog.data?.types.find((candidate) => candidate.type_id === typeId) ?? null;
  const attributes: ReadonlyMap<string, QueryAttribute> =
    type === null ? new Map() : attributeMap(type);

  if (detail.isLoading) {
    return (
      <p
        role="status"
        className="px-4 py-6 text-body-sm text-text-secondary"
        data-testid="record-detail-loading"
      >
        Loading record…
      </p>
    );
  }

  if (detail.isError) {
    return (
      <div role="alert" className="px-4 py-6" data-testid="record-detail-error">
        <p className="text-body text-destructive">Could not load this record</p>
        <p className="mt-1 text-body-sm text-text-secondary">{detail.error.message}</p>
      </div>
    );
  }

  const record = detail.data;
  if (record === undefined) return <></>;

  return (
    <>
      <SectionShell id="query-record-attributes" title="RECORD">
        <dl
          className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1 text-body-sm"
          data-testid="record-detail-attributes"
        >
          {Object.entries(record.attributes).map(([name, value]) => {
            const attribute = attributes.get(name);
            return (
              <div key={name} className="contents">
                <dt className="text-text-secondary">{attribute?.label ?? name}</dt>
                <dd
                  className="min-w-0 break-words text-text-primary"
                  data-testid={`record-detail-value-${name}`}
                >
                  {attribute === undefined ? (
                    // An attribute the catalog does not describe (a role
                    // change, or a type that gained a column): show the raw
                    // value rather than dropping the field silently.
                    <span className="font-mono text-text-secondary">
                      {value === null || value === undefined ? "—" : String(value)}
                    </span>
                  ) : (
                    renderQueryValue(attribute, value, producerNames)
                  )}
                </dd>
              </div>
            );
          })}
        </dl>
      </SectionShell>

      <SourceBytesSection typeId={typeId} recordId={recordId} />
    </>
  );
}
